import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import { loadSelectedSchool } from 'tio-common/utils/tuition-assistance/application-new-route';
import type Transition from '@ember/routing/transition';
import type RouterService from '@ember/routing/router-service';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';

export default class AuthenticatedTasConfirmationRoute extends Route {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;

  includes = ['tas-courses', 'tas-program-instance.tas-program-template'];

  beforeModel(transition: Transition) {
    if (transition.to?.queryParams.tasType === undefined) {
      this.router.transitionTo('authenticated.tas.dashboard');
    }
  }
  async model(params: { tasType: string; applicationId: string; evidence: boolean }) {
    const { content } = (await this.store.request(
      findRecord<TasApplicationModel>('tas-application', params.applicationId, {
        include: this.includes.join(','),
      })
    )) as { content: { data: TasApplicationModel } };
    const application = content.data;
    const showLpnSurvey = await this.shouldShowLpnSurvey(application);
    return { application, tasType: params.tasType, evidence: params.evidence, showLpnSurvey };
  }

  /**
   * The LPN discount survey is shown only when the application's school is in the
   * Learning Partner Network AND the company has LPN enabled — unless the company
   * has explicitly disabled that specific school (opt-out).
   */
  private async shouldShowLpnSurvey(application: TasApplicationModel): Promise<boolean> {
    const programInstance = application.tasProgramInstance;
    if (!programInstance) {
      return false;
    }

    // Company-wide LPN toggle must be on.
    const companySetting = this.sessionContext.currentEmployee?.company?.companySetting;
    if (!companySetting?.lpn?.isEnabled) {
      return false;
    }

    // Confirm the school is in the LPN network via its unified-institution record.
    // loadSelectedSchool includes the program-template filter, which the `lpn`
    // flag depends on.
    const institution = await loadSelectedSchool(programInstance, this.store);
    if (!institution?.lpn) {
      return false;
    }

    // Opt-out: show unless the company has an explicit, disabled setting for this
    // school. A confirmed LPN school always has an identifier (loadSelectedSchool
    // returns undefined otherwise), but guard on it so a missing identifier can
    // never accidentally match a settings row.
    const identifier = this.lpnIdentifierFor(programInstance);
    const settings = await companySetting.companyLpnSetting;
    const explicitlyDisabled = Boolean(
      identifier &&
      settings?.some(
        (setting) =>
          setting.lpnPage?.institutionIdentifier === identifier && setting.isEnabled === false
      )
    );
    return !explicitlyDisabled;
  }

  /**
   * Builds the LPN institution identifier (e.g. `dapip_123` / `tas_456`) the same
   * way the API does, so it can be matched against a company's LPN settings.
   */
  private lpnIdentifierFor(programInstance: TasProgramInstanceModel): string | undefined {
    const dapipId = programInstance.fields['DAPIP_INSTITUTION_ID']?.values?.[0];
    if (dapipId) {
      return `dapip_${dapipId}`;
    }
    const tasId = programInstance.fields['TAS_INSTITUTION_ID']?.values?.[0];
    if (tasId) {
      return `tas_${tasId}`;
    }
    return undefined;
  }
}
